import {
  Box,
  Skeleton,
  TextField,
  TableCell,
  Table,
  TableBody,
  TableRow,
  Paper,
  TableHead,
  Typography,
  TableContainer,
  IconButton,
  Button,
  Stack,
} from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import tableEmptyState from "../../../assets/images/common/tableEmptyState.svg";
import { setUsersSliceData } from "../../../redux/features/users/usersSlice";
import {
  editUserInUserGroup,
  deleteStagedUserGroup,
  fetchUserGroupBulkUploadReview,
} from "redux/actions/users/usersData";
import EditIcon from "../../../assets/images/common/editIconBlack.svg";
import DeleteIcon from "../../../assets/images/DeleteIcon.svg";
import { enqueueSnackbar } from "notistack";
import { ErrorOutline } from "@mui/icons-material";

// Update the getFieldError function to handle the array format properly
const getFieldError = (errors, fieldName) => {
  const fieldErrors = errors?.[fieldName];

  if (!fieldErrors || fieldErrors.length === 0) return null;

  // Handle array of error messages
  const formattedError = Array.isArray(fieldErrors)
    ? fieldErrors.join(", ")
    : fieldErrors;

  return (
    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <ErrorOutline style={{ fontSize: "18px" }} />
      {formattedError}
    </span>
  );
};

// Component for user group builder - step 1
const UserGroupBulkUploadEditForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { Id } = useParams();

  // Common states
  const userGroupCreating = useSelector((e) => e.users.userGroupCreating);
  const programsCreateSkeletonData = useSelector(
    (e) => e.programs.programsCreateSkeletonData
  );

  // Module create step 1 states
  const userGroupStep1ErrValidate = useSelector(
    (e) => e.users.userGroupStep1ErrValidate
  );
  const userGroupBulkUploadGroupName = useSelector(
    (e) => e.users.userGroupBulkUploadGroupName
  );
  const userGroupBulkUploadDescription = useSelector(
    (e) => e.users.userGroupBulkUploadDescription
  );

  const userGroupBulkUploadEditErrorList = useSelector(
    (e) => e.users.userGroupBulkUploadEditErrorList
  );

  const userGroupBulkUploadOrganizationName = useSelector(
    (e) => e.users.userGroupBulkUploadOrganizationName
  );

  const userGroupBulkUploadUploadUserList = useSelector(
    (e) => e.users.userGroupBulkUploadUploadUserList
  );

  // State to track which row is being edited
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [editedEmail, setEditedEmail] = React.useState("");
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [localUserEmails, setLocalUserEmails] = React.useState(null);

  const displayUserEmails =
    localUserEmails || userGroupBulkUploadUploadUserList;

  const handleFieldInputChange = (event, mtype) => {
    const {
      target: { value },
    } = event;
    if (
      mtype === "userGroupBulkUploadGroupName" ||
      mtype === "userGroupBulkUploadDescription" ||
      mtype === "userGroupBulkUploadOrganizationName"
    ) {
      dispatch(setUsersSliceData({ type: mtype, data: value }));

      const fieldMapping = {
        userGroupBulkUploadGroupName: "title",
        userGroupBulkUploadDescription: "description",
        userGroupBulkUploadOrganizationName: "organization_name",
      };

      const apiField = fieldMapping[mtype];
      if (apiField && userGroupBulkUploadEditErrorList?.[apiField]) {
        const updatedErrors = { ...userGroupBulkUploadEditErrorList };
        delete updatedErrors[apiField];

        dispatch(
          setUsersSliceData({
            type: "userGroupBulkUploadEditErrorList",
            data: updatedErrors,
          })
        );
      }
    } else {
      // Array values - On autofill we get a stringified value.
      dispatch(
        setUsersSliceData({
          type: mtype,
          data: typeof value === "string" ? value.split(",") : value,
        })
      );
    }
  };

  // Check if email is in invalid list
  const getEmailStatus = (email) => {
    const invalidEmails =
      userGroupBulkUploadEditErrorList?.user_emails?.["Invalid user emails"] ||
      userGroupBulkUploadEditErrorList?.user_emails?.[
        "Organization mismatch emails"
      ] ||
      [];
    return invalidEmails.includes(email) ? "Failed" : "Success";
  };

  // Handle edit button click
  const handleEditClick = (index, currentEmail) => {
    setEditingIndex(index);
    setEditedEmail(currentEmail);
  };

  const onDeleteBulkStagedUser = () => {
    dispatch(deleteStagedUserGroup({ id: Id })).then(({ payload, meta }) => {
      if (meta?.requestStatus && meta.requestStatus === "fulfilled") {
        enqueueSnackbar("Deleted successfully", { variant: "success" });
        dispatch(fetchUserGroupBulkUploadReview());
      } else {
        enqueueSnackbar("Couldn't delete users. Please try again later.", {
          variant: "error",
        });
      }
    });
  };

  // Handle email input change
  const handleEmailChange = (event) => {
    setEditedEmail(event.target.value);
  };

  const handleUpdateEmail = (index) => {
    const emailsToUpdate = localUserEmails || [
      ...userGroupBulkUploadUploadUserList,
    ];

    const updatedEmails = [...emailsToUpdate];
    updatedEmails[index] = editedEmail;

    setLocalUserEmails(updatedEmails);
    setEditingIndex(null);
    setEditedEmail("");

    enqueueSnackbar("Email updated locally. Click 'Update' to save changes.", {
      variant: "info",
    });
  };

  const handleOverallUpdate = async () => {
    dispatch(
      setUsersSliceData({
        type: "userGroupBulkUploadEditErrorList",
        data: {},
      })
    );

    // Validate required fields
    if (userGroupBulkUploadGroupName.length < 4) {
      enqueueSnackbar("Group name must be at least 4 characters", {
        variant: "error",
      });
      return;
    }

    if (userGroupBulkUploadDescription.length < 4) {
      enqueueSnackbar("Group description must be at least 4 characters", {
        variant: "error",
      });
      return;
    }

    if (!userGroupBulkUploadOrganizationName) {
      enqueueSnackbar("Organization name is required", {
        variant: "error",
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Use local emails if available, otherwise use Redux state
      const emailsToSend = localUserEmails || userGroupBulkUploadUploadUserList;

      await dispatch(
        editUserInUserGroup({
          Id,
          title: userGroupBulkUploadGroupName,
          description: userGroupBulkUploadDescription,
          organization_name: userGroupBulkUploadOrganizationName,
          user_emails: emailsToSend,
        })
      ).unwrap();

      enqueueSnackbar("User group updated successfully", {
        variant: "success",
      });

      // Clear local state after successful update
      setLocalUserEmails(null);
      navigate(-1);
    } catch (error) {
      console.error("Error updating user group:", error);

      if (error?.errors) {
        dispatch(
          setUsersSliceData({
            type: "userGroupBulkUploadEditErrorList",
            data: error.errors,
          })
        );
        enqueueSnackbar("Please fix the highlighted errors.", {
          variant: "error",
        });
      } else {
        enqueueSnackbar(
          error?.message || "Something went wrong. Please try later.",
          {
            variant: "error",
          }
        );
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle discard
  const handleDiscard = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ pb: 10 }}>
      <div className="animate__animated animate__fadeIn dt-ta-prog-modules-create-s1">
        {userGroupCreating ? (
          // Loading screen when questions are loading
          <div>
            {programsCreateSkeletonData.map((skData, index) => (
              <Skeleton
                key={index}
                sx={{ marginBottom: skData.marginBottom }}
                variant="rectangular"
                height={skData.height}
              />
            ))}
          </div>
        ) : (
          <Box
            component="form"
            sx={{
              maxWidth: "600px",
              width: "100%",
            }}
            className="dt-ta-prog-modules-create-s1-form"
            noValidate
            autoComplete="off">
            <TextField
              id="dtTaProgModCreateS1Org"
              label="Organization Name"
              variant="outlined"
              fullWidth
              style={{ marginTop: "16px" }}
              inputProps={{ maxLength: 200 }}
              placeholder="Enter Organization Name"
              value={userGroupBulkUploadOrganizationName}
              onChange={(event) => {
                handleFieldInputChange(
                  event,
                  "userGroupBulkUploadOrganizationName"
                );
              }}
              error={!!userGroupBulkUploadEditErrorList?.organization_name}
              helperText={getFieldError(
                userGroupBulkUploadEditErrorList,
                "organization_name"
              )}
            />
            <TextField
              error={
                (userGroupStep1ErrValidate &&
                  userGroupBulkUploadGroupName.length < 4) ||
                !!userGroupBulkUploadEditErrorList?.title
              }
              id="dtTaProgModCreateS1Name"
              label="Group Name"
              variant="outlined"
              fullWidth
              style={{ marginTop: "16px" }}
              inputProps={{ maxLength: 100 }}
              placeholder="Enter Group Name"
              helperText={
                getFieldError(userGroupBulkUploadEditErrorList, "title") ||
                (userGroupStep1ErrValidate &&
                userGroupBulkUploadGroupName.length < 4
                  ? "Min 4 characters needed"
                  : "")
              }
              value={userGroupBulkUploadGroupName}
              onChange={(event) => {
                handleFieldInputChange(event, "userGroupBulkUploadGroupName");
              }}
            />
            <TextField
              error={
                (userGroupStep1ErrValidate &&
                  userGroupBulkUploadDescription.length < 4) ||
                !!userGroupBulkUploadEditErrorList?.description
              }
              id="dtTaProgModCreateS1Desc"
              label="Group Description"
              multiline
              fullWidth
              style={{ marginTop: "16px" }}
              placeholder="Enter Description"
              helperText={
                getFieldError(
                  userGroupBulkUploadEditErrorList,
                  "description"
                ) ||
                (userGroupStep1ErrValidate &&
                userGroupBulkUploadDescription.length < 4
                  ? "Min 4 characters needed"
                  : "")
              }
              inputProps={{ maxLength: 1000 }}
              rows={4}
              value={userGroupBulkUploadDescription}
              onChange={(event) => {
                handleFieldInputChange(event, "userGroupBulkUploadDescription");
              }}
            />
          </Box>
        )}
        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          {displayUserEmails.length} Selected User(s)
        </Typography>

        <TableContainer component={Paper} sx={{ mt: 2, mb: 10 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell align="left">Email</TableCell>
                <TableCell align="left">Status</TableCell>
                <TableCell align="left">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayUserEmails.map((user, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row" align="left">
                    {editingIndex === index ? (
                      <TextField
                        value={editedEmail}
                        onChange={handleEmailChange}
                        variant="outlined"
                        size="small"
                        fullWidth
                        autoFocus
                      />
                    ) : user ? (
                      `${user}`
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "25%",
                      wordBreak: "break-word",
                      color:
                        getEmailStatus(user) === "Failed"
                          ? "#d32f2f"
                          : "#2e7d32",
                    }}>
                    {user ? getEmailStatus(user) : "--"}
                  </TableCell>

                  <TableCell>
                    {editingIndex === index ? (
                      <>
                        <IconButton
                          color="primary"
                          sx={{ mr: 2 }}
                          onClick={() => handleUpdateEmail(index)}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Save
                          </Typography>
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() => {
                            setEditingIndex(null);
                            setEditedEmail("");
                          }}>
                          <Typography variant="body2">Cancel</Typography>
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          color="primary"
                          sx={{ mr: 2 }}
                          onClick={() => handleEditClick(index, user)}>
                          <img src={EditIcon} alt="Edit" />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={onDeleteBulkStagedUser}>
                          <img src={DeleteIcon} alt="Delete" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {displayUserEmails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <div
                      className="flex-column flex-jcenter flex-acenter"
                      style={{ margin: "3rem auto", display: "flex" }}>
                      <img src={tableEmptyState} alt="No users" />
                      <Typography
                        variant="h6"
                        className="flex-jcenter flex-acente">
                        No Users Found
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          className="dt-ta-prog-modules-create-sbtns"
          spacing={2}
          direction="row">
          <Button
            className="dt-ta-prog-modules-create-sbtns-out"
            variant="outlined"
            disabled={isUpdating}
            onClick={handleDiscard}>
            Discard
          </Button>
          <Button
            className="dt-ta-prog-modules-create-sbtns-filled"
            variant="contained"
            disabled={isUpdating}
            onClick={handleOverallUpdate}>
            {isUpdating ? (
              <div className="dot-flashing dot-flashing-t1 dt-ta-login-cta-ldr"></div>
            ) : (
              "Update"
            )}
          </Button>
        </Stack>
      </div>
    </Box>
  );
};

export default UserGroupBulkUploadEditForm;
